import React, { useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { GiHamburgerMenu } from 'react-icons/gi';
import { BellIcon, CircleAlertIcon, Settings, UserCircle, X, Star, ChevronDown, ArrowLeftRight, LogOut } from 'lucide-react';
import {
  Menu as NavMenu,
  MenuHandler,
  MenuList,
  MenuItem as NavMenuItem,
  Avatar,
  Typography,
  Button,
  IconButton
} from '@material-tailwind/react';
import { useParams, useRouter } from 'next/navigation';
import { useAppDispatch, useAppSelector } from 'features/hooks';
import { RootState } from 'features/store';
import { logOutAndClearReminder, setSwitchedListing } from 'features/authSlice';
import { resetModal, setOpenAccountModal, setOpenUpdatePaymentModal } from 'features/generalSlice';
import { isUserAvailable } from 'helpers/authUser';
import { UserProfileQuery, useListingDetailQuery, useSwitchUserDefaultListingMutation, useCancelSubscriptionMutation, useGetStripePricesQuery } from 'graphql/generated/graphql';
import { AccountManagementModal } from './account-management-modal';
import { isProfessionalTier } from '@/lib/tier-features';
import { UpgradePaymentModal } from './modals/upgrade-payment-modal';
import { UpdatePaymentModal } from './modals/update-payment-modal';
import { UpgradeSuccessModal } from './modals/upgrade-success-modal';
import { FormDataProvider } from 'hook/useFormData';
import { toast } from 'react-toastify';
import { ConfirmDialog } from './confirm-dialog';
import { Elements } from '@stripe/react-stripe-js';
import { getStripe } from '@/lib/stripe';


interface HeaderProps {
  setToggled: (value: boolean) => void;
  toggled: boolean;
  handlePasswordOpen: () => void;
  handleSwitchOpen: () => void;
  handleProfileSettingsOpen: () => void;
  handleExternalSidebarToggle?: () => void;
  userDetail: UserProfileQuery | undefined;
}

const Header: React.FC<HeaderProps> = ({
  setToggled,
  toggled,
  handlePasswordOpen,
  handleSwitchOpen,
  handleProfileSettingsOpen,
  handleExternalSidebarToggle,
  userDetail
}) => {
  const { user } = useAppSelector((state: RootState) => state.culineerAuth);
  const dispatch = useAppDispatch();
  const router = useRouter();
  const params = useParams();
  const userAvailable = isUserAvailable(user);

  const [accountModalOpen, setAccountModalOpen] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [updatePaymentModalOpen, setUpdatePaymentModalOpen] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [isTaxExempt, setIsTaxExempt] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // Listen for banner-triggered modal opens (dispatched by DowngradeBanner)
  const openAccountModalFlag = useAppSelector((state: RootState) => state.general.openAccountModal)
  const openUpdatePaymentModalFlag = useAppSelector((state: RootState) => state.general.openUpdatePaymentModal)

  useEffect(() => {
    if (openAccountModalFlag) {
      setAccountModalOpen(true)
      dispatch(setOpenAccountModal(false))
    }
  }, [openAccountModalFlag, dispatch])

  useEffect(() => {
    if (openUpdatePaymentModalFlag) {
      setUpdatePaymentModalOpen(true)
      dispatch(setOpenUpdatePaymentModal(false))
    }
  }, [openUpdatePaymentModalFlag, dispatch])
  const [switchDefaultListing] = useSwitchUserDefaultListingMutation({ fetchPolicy: 'no-cache' })
  const [cancelSubscription, { loading: cancelLoading }] = useCancelSubscriptionMutation({ fetchPolicy: 'no-cache' })
  const { data: pricingData } = useGetStripePricesQuery({ fetchPolicy: 'no-cache' })

  const handleLogout = useCallback(async () => {
    localStorage.removeItem('modalShown');
    dispatch(resetModal());
    dispatch(logOutAndClearReminder())
    router.push('/');
  }, [dispatch, router]);

  const toggleSidebar = useCallback(() => {
    setToggled(!toggled);
  }, [toggled, setToggled]);

  const switchListingDirectory = async (listingID: number) => {
    try {
      const response = await switchDefaultListing({
        variables: {
          listingID
        },
      })
      if (response !== undefined) {
        dispatch(setSwitchedListing(response.data?.switchUserDefaultListing))
        return
      }
    } catch (error: any) {
      console.error('Error switching listing directory:', error.message)
    }
  }

  const { data: listing } = useListingDetailQuery({
    fetchPolicy: 'no-cache',
    variables: {
      listingId: userDetail?.userProfile.default_listing_id,
    },
    skip: !userDetail?.userProfile?.default_listing_id,
  })

  const getProfileSrc = (): string | undefined => {
    let profileImgUrl: string | undefined
    if (userDetail?.userProfile?.user_type?.toLowerCase() === 'general') {
      profileImgUrl = userDetail?.userProfile?.profile_photo_resource || '/assets/avatar/user-default.svg'
    } else if (userDetail?.userProfile?.user_type?.toLowerCase() === 'producer') {
      profileImgUrl = listing?.listingDetail?.photo || '/assets/icons/tractor.svg'
    } else if (userDetail?.userProfile?.user_type?.toLowerCase() === 'market') {
      profileImgUrl = listing?.listingDetail?.photo || '/assets/icons/Market.svg'
    }

    return profileImgUrl
  }

  const handleUpgrade = () => {
    // Close account modal and open payment modal
    setAccountModalOpen(false)
    setPaymentModalOpen(true)
  }

  const handlePaymentSuccess = (taxExempt: boolean) => {
    // Store tax exempt status and show success modal
    setIsTaxExempt(taxExempt)
    setPaymentModalOpen(false)
    setSuccessModalOpen(true)
  }

  const handleManageSubscription = () => {
    setAccountModalOpen(false)
    setUpdatePaymentModalOpen(true)
  }

  const handleUpdatePaymentSuccess = () => {
    setUpdatePaymentModalOpen(false)
    // Toast shown in modal - no need to duplicate
  }

  const handleCancelSubscription = () => {
    setAccountModalOpen(false)
    setShowCancelConfirm(true)
  }

  const confirmCancelSubscription = async () => {
    try {
      const result = await cancelSubscription()

      if (result.data?.cancelSubscription?.success) {
        toast.success(result.data.cancelSubscription.message || 'Subscription canceled successfully')
        setShowCancelConfirm(false)

        // Refresh listing data to show updated status
        setTimeout(() => {
          router.refresh()
        }, 2000)
      } else {
        toast.error(result.data?.cancelSubscription?.message || 'Failed to cancel subscription')
      }
    } catch (error) {
      console.error('Cancel subscription error:', error)
      toast.error('An error occurred while canceling your subscription')
    }
  }

  // Prepare user data for payment modal
  const getUserData = () => {
    const defaultListing = user?.default_listing || listing?.listingDetail
    return {
      firstName: defaultListing?.firstName || defaultListing?.first_name || user?.first_name || '',
      lastName: defaultListing?.lastName || defaultListing?.last_name || user?.last_name || '',
      email: defaultListing?.email || user?.email_address || '',
      phone: defaultListing?.phone || user?.phone_number || '',
      address: defaultListing?.address || '',
      city: defaultListing?.city || user?.city || '',
      state: defaultListing?.state || user?.state || '',
      zip: defaultListing?.zipcode || user?.zipcode || '',
    }
  }

  // Get user slug for success modal
  const getUserSlug = () => {
    return listing?.listingDetail?.slug || user?.default_listing?.slug || user?.slug || ''
  }

  // Get subscription data for modal
  const getSubscriptionData = () => {
    const interval = user?.billing_interval === 'year' ? 'year' : 'month'

    // Get fetched prices
    const stripeMonthlyPrice = pricingData?.stripePrices?.find(
      (p) => p.interval === 'month' && p.active
    )?.unitAmount
    const stripeYearlyPrice = pricingData?.stripePrices?.find(
      (p) => p.interval === 'year' && p.active
    )?.unitAmount

    const monthlyPrice = stripeMonthlyPrice ? stripeMonthlyPrice / 100 : 15.00
    const yearlyPrice = stripeYearlyPrice ? stripeYearlyPrice / 100 : 150.00

    const amount = user?.subscription_amount || (interval === 'year' ? yearlyPrice : monthlyPrice)

    let nextPaymentDate: number | undefined
    if (user?.current_period_end) {
      nextPaymentDate = new Date(user.current_period_end).getTime() / 1000
    }

    return {
      interval: interval as 'month' | 'year',
      nextPaymentAmount: amount,
      nextPaymentDate: nextPaymentDate
    }
  }

  const renderUserMenu = () => (
    <NavMenu placement="bottom-end">
      <MenuHandler>
        <div className="flex cursor-pointer items-center gap-2">
          {(user?.user_id === userDetail?.userProfile?.user_id) ? (
            <Avatar
              variant="circular"
              alt={user?.full_name?.replace(' ', '-')}
              className="h-8 w-8"
              src={getProfileSrc()}
            />
          ) : (
            <Avatar
              variant="circular"
              alt={user?.full_name?.replace(' ', '-')}
              className="h-8 w-8"
              src={getProfileSrc()}
            />
          )}
          <div className="hidden items-center gap-1 sm:flex">
            <Typography variant="small" className="font-medium">
              {user?.user_type?.toLowerCase() === 'general' ? (user?.first_name || user?.full_name) : (user?.default_listing?.firstName || user?.default_listing?.name)}
            </Typography>
            <ChevronDown size={16} strokeWidth={2.5} />
          </div>
        </div>
      </MenuHandler>
      <MenuList className="min-w-[200px] p-1">
        {user?.listings?.length > 1 ? (
          user?.listings?.map((listing: any, index: number) => (
            <NavMenuItem
              key={`${listing?.listing_id}-${index}`}
              className={`flex items-center gap-2 ${listing?.slug === params?.slug ? 'bg-gray-200' : ''}`}
              onClick={() => {
                switchListingDirectory(listing?.listing_id)
                router.push(`/${listing?.user_type?.toLowerCase()}/${listing?.slug}`)
              }}
            >
              <div className="flex items-center gap-2">
                <Typography variant="small" className="truncate">
                  {listing?.name} - {listing.state} profile
                </Typography>
              </div>
            </NavMenuItem>
          ))
        ) : (
          <NavMenuItem className="flex items-center gap-2">
            <Link
              href={
                userDetail?.userProfile?.user_type?.toLowerCase() === 'general'
                  ? `/member/${userDetail?.userProfile?.slug}`
                  : `/${userDetail?.userProfile?.user_type?.toLowerCase()}/${listing?.listingDetail?.slug || userDetail?.userProfile?.default_listing?.slug}`
              }
              className="flex w-full items-center gap-2"
            >
              <UserCircle size={20} />
              <Typography variant="small">My Profile</Typography>
            </Link>
          </NavMenuItem>
        )}

        {user?.userRoles?.length > 1 && (
          <div role="none" className="outline-none">
            <hr className="my-2 border-blue-gray-50" />
            <NavMenuItem
              className="flex items-center gap-2"
              onClick={handleSwitchOpen}
            >
              <ArrowLeftRight size={20} />
              <Typography variant="small">Switch Role</Typography>
            </NavMenuItem>
          </div>
        )}

        {/* Account Management - For Producers and Markets */}
        {(userDetail?.userProfile?.user_type?.toLowerCase() === 'producer' ||
          userDetail?.userProfile?.user_type?.toLowerCase() === 'market') && (
            <div role="none" className="outline-none">
              <hr className="my-2 border-blue-gray-50" />
              <NavMenuItem
                className="flex items-center gap-1.5"
                onClick={() => setAccountModalOpen(true)}
              >
                <div className="w-5 h-5 relative bg-[#85c349]/10 rounded-md flex items-center justify-center">
                  <Star className="w-3 h-3 text-[#85c349]" fill="currentColor" />
                </div>
                <Typography variant="small" className="font-normal font-['Inter']">
                  {isProfessionalTier(
                    listing?.listingDetail?.membership_type,
                    listing?.listingDetail?.enhanced
                  )
                    ? 'My Plan'
                    : 'Upgrade to Pro'}
                </Typography>
              </NavMenuItem>
            </div>
          )}

        <hr className="my-2 border-blue-gray-50" />
        <NavMenuItem
          className="flex items-center gap-2"
          onClick={handleProfileSettingsOpen}
        >
          <Settings size={20} />
          <Typography variant="small">Account Settings</Typography>
        </NavMenuItem>

        <hr className="my-2 border-blue-gray-50" />
        {/* <NavMenuItem
          className="flex items-center gap-2"
          onClick={handlePasswordOpen}
        >
          <LockKey size={20} />
          <Typography variant="small">Change Password</Typography>
        </NavMenuItem> */}

        <hr className="my-2 border-blue-gray-50" />
        <NavMenuItem
          className="flex items-center gap-2 text-red-500 hover:text-red-700"
          onClick={handleLogout}
        >
          <LogOut size={20} />
          <Typography variant="small">Log Out</Typography>
        </NavMenuItem>
      </MenuList>
    </NavMenu>
  );

  const renderAuthButtons = () => (
    <div className="flex items-center gap-4">
      <Typography variant="small" className="hidden sm:block text-gray-600">
        Join our Culineer community!
      </Typography>
      <div className="flex gap-2">
        <Link href="/auth/join-culineer">
          <Button
            variant="outlined"
            size="sm"
            className="border-[#85C349] text-[#85C349] hover:bg-gray-50 rounded-full"
          >
            Join Culineer
          </Button>
        </Link>
        <Link href="/auth/login">
          <Button
            size="sm"
            className="bg-[#85C349] text-white shadow-md transition-all duration-300 flex-shrink-0 ease-in-out hover:scale-105 hover:bg-[#75B339] hover:shadow-lg rounded-full"
          >
            Sign In
          </Button>
        </Link>
      </div>
    </div>
  );

  return (
    <header className="sticky top-0 h-16 bg-white z-10" style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)' }}>
      <div className="mx-auto flex h-full items-center justify-between px-4">
        <div className="flex items-center gap-4">
          <IconButton
            variant="text"
            className="hidden sm:flex lg:hidden"
            onClick={toggleSidebar}
          >
            {toggled ? <X size={20} /> : <GiHamburgerMenu size={20} />}
          </IconButton>

          <Link href={user?.user_type?.toLowerCase() === 'producer' ? '/dashboard' : '/'}>
            <Image
              alt="Culineer Logo"
              src="/assets/logo.svg"
              width={120}
              height={40}
              className="h-auto w-[120px]"
              priority
            />
          </Link>
        </div>

        <div className="flex items-center gap-4">
          {userAvailable && (
            <div className='flex px-2'>
              <IconButton variant="text">
                <BellIcon size={24} className='h-5 w-5' />
              </IconButton>
              <IconButton variant='text' onClick={handleExternalSidebarToggle}>
                <CircleAlertIcon size={24} className='h-5 w-5 rotate-180' />
              </IconButton>
            </div>
          )}

          {userAvailable ? renderUserMenu() : renderAuthButtons()}
        </div>
      </div>

      {/* Account Management Modal */}
      {(userDetail?.userProfile?.user_type?.toLowerCase() === 'producer' ||
        userDetail?.userProfile?.user_type?.toLowerCase() === 'market') &&
        accountModalOpen && listing && (
          <AccountManagementModal
            open={accountModalOpen}
            onClose={() => setAccountModalOpen(false)}
            membershipType={listing?.listingDetail?.membership_type}
            enhanced={listing?.listingDetail?.enhanced}
            subscriptionData={getSubscriptionData()}
            onUpgrade={handleUpgrade}
            onManageSubscription={handleManageSubscription}
            onCancelSubscription={handleCancelSubscription}
          />
        )}

      {/* Payment Modal */}
      {paymentModalOpen && (
        <FormDataProvider>
          <UpgradePaymentModal
            open={paymentModalOpen}
            onClose={() => setPaymentModalOpen(false)}
            onSuccess={handlePaymentSuccess}
            userData={getUserData()}
          />
        </FormDataProvider>
      )}

      {/* Update Payment Modal */}
      {updatePaymentModalOpen && (
        <Elements stripe={getStripe()}>
          <UpdatePaymentModal
            open={updatePaymentModalOpen}
            onClose={() => setUpdatePaymentModalOpen(false)}
            onSuccess={handleUpdatePaymentSuccess}
            userData={getUserData()}
          />
        </Elements>
      )}

      {/* Success Modal */}
      {successModalOpen && (
        <UpgradeSuccessModal
          open={successModalOpen}
          onClose={() => {
            setSuccessModalOpen(false)
            router.refresh()
          }}
          userSlug={user?.slug || ''}
          listingSlug={listing?.listingDetail?.slug || userDetail?.userProfile?.default_listing?.slug || ''}
          userType={userDetail?.userProfile?.user_type?.toLowerCase() || 'general'}
          isTaxExempt={isTaxExempt}
        />
      )}

      {/* Cancel Subscription Confirmation */}
      <ConfirmDialog
        open={showCancelConfirm}
        onOpenChange={setShowCancelConfirm}
        title="Cancel Subscription"
        description="Your subscription will be canceled at the end of your current billing period. You'll downgrade to Verified Business but retain Professional access until then. Are you sure?"
        confirmText={cancelLoading ? "Canceling..." : "Yes, Cancel Subscription"}
        cancelText="Keep Subscription"
        onConfirm={confirmCancelSubscription}
        variant="destructive"

      />
    </header>
  );
};

export default Header;